import { useEffect, useState } from 'react';
import { Table, Tag, Select, Button, Space, message, Typography } from 'antd';
import { SyncOutlined, FileTextOutlined, LinkOutlined } from '@ant-design/icons';
import { PageHeader, StatCard, StatRow } from '../../components/shared';
import { akimatApi, AkimatDoc, DocType } from '../../api/akimat';

const { Link } = Typography;

export default function AkimatDocuments() {
  const [items, setItems] = useState<AkimatDoc[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size] = useState(20);
  const [type, setType] = useState<string | undefined>();
  const [types, setTypes] = useState<DocType[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await akimatApi.getDocuments({ type, page, size });
      setItems(res.items);
      setTotal(res.total);
    } catch {
      message.error('Не удалось загрузить документы');
    } finally {
      setLoading(false);
    }
  };

  const loadTypes = async () => {
    try {
      setTypes(await akimatApi.getDocTypes());
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, page]);

  const onSync = async () => {
    setSyncing(true);
    try {
      const res = await akimatApi.syncDocuments();
      message.success(`Синхронизация: новых ${res.new}, обновлено ${res.updated}`);
      await loadTypes();
      setPage(1);
      await load();
    } catch {
      message.error('Ошибка синхронизации');
    } finally {
      setSyncing(false);
    }
  };

  const columns = [
    {
      title: 'Документ',
      dataIndex: 'title',
      key: 'title',
      render: (t: string, r: AkimatDoc) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{t}</span>
          {r.file_url && (
            <Link href={r.file_url} target="_blank">
              <LinkOutlined /> Открыть файл
            </Link>
          )}
        </Space>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 160,
      render: (t?: string) => (t ? <Tag>{t}</Tag> : '—'),
    },
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (d?: string) => (d ? new Date(d).toLocaleDateString('ru-RU') : '—'),
    },
    {
      title: 'Просмотры',
      dataIndex: 'views',
      key: 'views',
      width: 110,
      render: (v?: number) => v ?? 0,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Документы акимата"
        subtitle="Зеркало документов Бурлинского района с gov.kz (синхрон раз в сутки)"
        extra={
          <Button
            type="primary"
            icon={<SyncOutlined spin={syncing} />}
            loading={syncing}
            onClick={onSync}
          >
            Синхронизировать сейчас
          </Button>
        }
      />

      <StatRow span={8}>
        <StatCard label="Всего документов" value={total} icon={<FileTextOutlined />} />
        <StatCard label="Типов" value={types.length} color="var(--l-accent)" colorValue />
        <StatCard
          label="Самый частый тип"
          value={types[0] ? `${types[0].type} (${types[0].count})` : '—'}
        />
      </StatRow>

      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Все типы"
          style={{ width: 260 }}
          value={type}
          onChange={(v) => {
            setPage(1);
            setType(v);
          }}
          options={types.map((t) => ({
            value: t.type,
            label: `${t.type} · ${t.count}`,
          }))}
        />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={{
          current: page,
          pageSize: size,
          total,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />
    </div>
  );
}
